import { translateApp } from "@/i18n";
// =============================================================================
// TABS
// =============================================================================
/**
 * Each tab maps to at most one reservationStatus + one fulfillmentStatus
 * server-side filter (the API only accepts a single enum value per param,
 * not lists, so composite tabs aren't expressible). Itinerary bookings derive
 * reservationStatus from the itinerary state (PENDING/APPROVED ->
 * PENDING_CONFIRMATION, PAID -> CONFIRMED, REJECTED/CANCELLED/EXPIRED ->
 * CANCELLED), so fulfillment tabs are scoped to confirmed reservations.
 */
export const RESERVATION_TABS = [
    {
        id: "ALL",
        labelKey: "reservations.auto.todos",
        reservationStatus: null,
        fulfillmentStatus: null,
    },
    {
        id: "PENDING_CONFIRMATION",
        labelKey: "reservations.auto.por_confirmar",
        reservationStatus: "PENDING_CONFIRMATION",
        fulfillmentStatus: null,
    },
    {
        id: "CONFIRMED",
        labelKey: "reservations.auto.confirmadas",
        reservationStatus: "CONFIRMED",
        fulfillmentStatus: "ACKNOWLEDGED",
    },
    {
        id: "IN_PROGRESS",
        labelKey: "reservations.auto.en_curso",
        reservationStatus: "CONFIRMED",
        fulfillmentStatus: "IN_PROGRESS",
    },
    {
        id: "COMPLETED",
        labelKey: "reservations.auto.finalizadas",
        reservationStatus: "CONFIRMED",
        fulfillmentStatus: "COMPLETED",
    },
    {
        id: "CANCELLED",
        labelKey: "reservations.auto.canceladas",
        reservationStatus: "CANCELLED",
        fulfillmentStatus: null,
    },
];
export function getTabFilters(tabId) {
    const tab = RESERVATION_TABS.find((t) => t.id === tabId) ?? RESERVATION_TABS[0];
    return {
        reservationStatus: tab.reservationStatus,
        fulfillmentStatus: tab.fulfillmentStatus,
    };
}
// =============================================================================
// DISPLAY STATUS
// =============================================================================
/**
 * Collapses the API's two independent status axes into the single badge/label
 * the UI shows per booking. Priority: any cancellation (booking-level or
 * fulfillment-level, e.g. a no-show) wins; then an unconfirmed reservation;
 * otherwise fulfillmentStatus is shown as-is.
 */
export function getDisplayStatus(booking) {
    if (booking.reservationStatus === "CANCELLED" ||
        booking.fulfillmentStatus === "CANCELLED") {
        return "CANCELLED";
    }
    if (booking.reservationStatus === "PENDING_CONFIRMATION") {
        return "PENDING_CONFIRMATION";
    }
    return booking.fulfillmentStatus;
}
/**
 * Provider fulfillment actions (acknowledge/start/complete) are only valid
 * once the reservation is confirmed (itinerary PAID); the API rejects them
 * otherwise.
 */
export function canUpdateFulfillment(booking) {
    return booking.reservationStatus === "CONFIRMED";
}
// =============================================================================
// STATE HELPERS
// =============================================================================
export function getStateTranslationKey(status) {
    switch (status) {
        case "PENDING_CONFIRMATION":
            return "reservations.state.pending_confirmation";
        case "PENDING":
            return "reservations.state.pending";
        case "ACKNOWLEDGED":
            return "reservations.state.confirmed";
        case "IN_PROGRESS":
            return "reservations.state.in_progress";
        case "COMPLETED":
            return "reservations.state.completed";
        case "CANCELLED":
            return "reservations.state.cancelled";
        default:
            return "reservations.state.pending";
    }
}
export function getState(status) {
    return translateApp(getStateTranslationKey(status));
}
export function getStateBadgeClass(status) {
    switch (status) {
        case "PENDING_CONFIRMATION":
        case "PENDING":
            return "bg-amber-50 text-amber-700 border border-amber-200";
        case "ACKNOWLEDGED":
            return "bg-[#ECFDF5] text-[#059669] border border-[#6EE7B7]";
        case "IN_PROGRESS":
            return "bg-indigo-50 text-indigo-700 border border-indigo-200";
        case "COMPLETED":
            return "bg-[#ECFDF5] text-[#059669] border border-[#6EE7B7]";
        case "CANCELLED":
            return "bg-gray-100 text-gray-600 border border-gray-200";
        default:
            return "bg-gray-100 text-gray-600 border border-gray-200";
    }
}
export function getStateDotClass(status) {
    switch (status) {
        case "PENDING_CONFIRMATION":
        case "PENDING":
            return "bg-amber-500";
        case "ACKNOWLEDGED":
            return "bg-[#10B981]";
        case "IN_PROGRESS":
            return "bg-indigo-500";
        case "COMPLETED":
            return "bg-[#10B981]";
        case "CANCELLED":
            return "bg-gray-400";
        default:
            return "bg-gray-400";
    }
}
// =============================================================================
// ICON HELPER
// =============================================================================
export const TRANSPORTATION_SERVICE_TYPE_CODE = "transportation";
/**
 * Returns the icon name (Tabler Icons) for a service type code (slug).
 */
export function getServiceIcon(serviceTypeCode) {
    switch (serviceTypeCode) {
        case TRANSPORTATION_SERVICE_TYPE_CODE:
            return "IconBus";
        default:
            return "IconCalendarEvent";
    }
}
function readCustomField(values, key) {
    const value = values[key];
    if (value === null || value === undefined || value === "")
        return null;
    return String(value);
}
/**
 * Extracts transport pickup/dropoff values from a booking's custom fields.
 * Legacy keys (transportation_*) are normalized to canonical keys by the API.
 * Returns null when no transport value is present.
 */
export function getTransportDetails(booking) {
    const values = booking.customFieldValues;
    if (!values)
        return null;
    const details = {
        pickupLocation: readCustomField(values, "pickup_location"),
        pickupTime: readCustomField(values, "pickup_time"),
        dropoffLocation: readCustomField(values, "dropoff_location"),
    };
    return Object.values(details).some((value) => value !== null) ? details : null;
}
// =============================================================================
// DISPLAY HELPERS
// =============================================================================
/** BookingDto has no human-readable code — derive a short reference from the id. */
export function formatBookingReference(id) {
    return `#${id.slice(0, 8).toUpperCase()}`;
}
// =============================================================================
// EXPORT
// =============================================================================
export const reservationService = {
    RESERVATION_TABS,
    getTabFilters,
    getDisplayStatus,
    canUpdateFulfillment,
    getTransportDetails,
    getState,
    getStateTranslationKey,
    getServiceIcon,
    getStateBadgeClass,
    getStateDotClass,
    formatBookingReference,
};
export default reservationService;
